import { ClipUtils, getAssetPreviewType } from '@air/utils-clips';

import { CANVAS_SUPPORTED_IMAGE_EXTENSIONS } from '../uploadMimeTypes';
import {
  type CanvasAssetPayload,
  type FilterImageAssetsResult,
  type VideoRejectionReason,
} from './canvasAssetDropTypes';
import { VIDEO_TO_GIF_MAX_DURATION_SECONDS, VIDEO_TO_GIF_MAX_FILE_SIZE_BYTES } from './videoToGifLimits';

/**
 * Returns the rejection reason if the video exceeds limits, or null if it's within limits.
 * Videos missing size or duration are rejected as 'unknown'.
 */
function getVideoRejectionReason(asset: CanvasAssetPayload): VideoRejectionReason | null {
  if (asset.size === undefined || asset.duration === undefined) {
    return 'unknown';
  }

  const tooLarge = asset.size > VIDEO_TO_GIF_MAX_FILE_SIZE_BYTES;
  const tooLong = asset.duration > VIDEO_TO_GIF_MAX_DURATION_SECONDS;

  if (tooLarge && tooLong) {
    return 'both';
  }
  if (tooLarge) {
    return 'size';
  }
  if (tooLong) {
    return 'duration';
  }

  return null;
}

/**
 * Filters payloads to canvas-eligible assets: images (photos) and GIF-eligible videos.
 * Processing/uploading clips are treated as unsupported.
 * Videos that exceed backend limits (500 MB / 30s) are tracked separately for toast messaging.
 * Returns counts for toast messaging.
 */
export function filterImageAssets(allAssets: CanvasAssetPayload[]): FilterImageAssetsResult {
  const imageAssets: CanvasAssetPayload[] = [];
  const videoAssets: CanvasAssetPayload[] = [];
  const videoRejections: VideoRejectionReason[] = [];

  for (const asset of allAssets) {
    if (ClipUtils.isProcessing(asset.status)) {
      continue;
    }

    const previewType = getAssetPreviewType({ ext: asset.ext, type: asset.type });

    if (
      previewType === 'photo' &&
      (asset.media?.image || asset.media?.original) &&
      CANVAS_SUPPORTED_IMAGE_EXTENSIONS.has(asset.ext?.toLowerCase() ?? '')
    ) {
      imageAssets.push(asset);
    } else if (previewType === 'video' && asset.media?.original) {
      const rejection = getVideoRejectionReason(asset);

      if (rejection) {
        videoRejections.push(rejection);
      } else {
        videoAssets.push(asset);
      }
    }
  }

  const addedCount = imageAssets.length + videoAssets.length;

  return {
    addedCount,
    imageAssets,
    unsupportedCount: allAssets.length - addedCount - videoRejections.length,
    videoAssets,
    videoRejections,
  };
}
